import { DateTime as LuxonDateTime } from "luxon";
import type TemporalUnit from "./TemporalUnit";
import type DateTimeOptions from "./DateTimeOptions";
import getCurrentDate from "./getCurrentDate";
import type { DifferenceOptions } from "./DifferenceOptions";
import type DateFormat from "./DateFormat";
import DEFAULT_LOCALE from "./DEFAULT_LOCALE";
import hoursToMinutes from "./hoursToMinutes";
import { MINUTES_PER_DAY, MINUTES_PER_HOUR } from "./constants";
import isString from "../strings/isString";

export type DateLikeInput = Date | DateTime | string;

type Values = {
  [key in TemporalUnit]?: number;
};

type PlusConfig = {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
};

type DifferenceUnit =
  | "calendarDays"
  | "days"
  | "weeks"
  | "minutes"
  | "years"
  | "hours"
  | "months";

type Unit = keyof LuxonDateTime | "minuteOfDay";

export type DateTime = {
  isInPast: () => boolean;
  isBefore: (other: DateTime) => boolean;
  toLuxonDateTime: () => LuxonDateTime;
  get: (unit: Unit) => number;
  set: (values: Values) => DateTime;
  /**
   * Sets the wall clock time of the day, as opposed to adding minutes to
   * midnight which differs on days with a DST switch.
   **/
  setMinuteOfDay: (minuteOfDay: number) => DateTime;
  plus: (config: PlusConfig) => DateTime;
  minus: (config: PlusConfig) => DateTime;
  difference: (
    other: DateLikeInput,
    unit: DifferenceUnit,
    options?: DifferenceOptions,
  ) => number;
  toJSDate: () => Date;
  /**
   * ISO 8601 in the instance's own time zone, including the offset.
   *
   * @example 2023-01-01T10:00:00.000+01:00
   **/
  toISOString: () => string;
  startOf: (unit: TemporalUnit) => DateTime;
  endOf: (unit: TemporalUnit) => DateTime;
  format: (format: DateFormat) => string;
  isSame: (other: Date | DateTime, unit: TemporalUnit) => boolean;
  setZone: (zone: string, opts?: { keepLocalTime?: boolean }) => DateTime;
  offsetNameShort: () => string;
  offset: () => number;
};

export default function dateTime(
  date: DateLikeInput = getCurrentDate(),
  options: DateTimeOptions = { locale: DEFAULT_LOCALE },
): DateTime {
  const instance = toLuxonDate(date, options);
  return fromLuxon(instance, options);
}

export const ROUNDING_METHODS = {
  truncate: (value: number) => Math.trunc(value),
  none: (value: number) => value,
};

function fromLuxon(
  instance: LuxonDateTime,
  options: DateTimeOptions = {},
): DateTime {
  return {
    offset: () => instance.offset,
    offsetNameShort: () => instance.offsetNameShort as string,
    isInPast: () => LuxonDateTime.now().diff(instance).as("milliseconds") > 0,
    isBefore: (other) =>
      instance.diff(other.toLuxonDateTime()).as("milliseconds") < 0,
    format: (formatStr: DateFormat) => instance.toFormat(formatStr, options),
    setZone: (zone: string, opts) => fromLuxon(instance.setZone(zone, opts)),
    toLuxonDateTime: () => instance,
    get: (unit) => {
      switch (unit) {
        case "minuteOfDay":
          return hoursToMinutes(instance.get("hour")) + instance.get("minute");
        default:
          return instance.get(unit);
      }
    },
    set: (values: Values) => fromLuxon(instance.set(values)),
    setMinuteOfDay: (minuteOfDay: number) => {
      const minute = minuteOfDay % MINUTES_PER_DAY;
      const day = instance
        .startOf("day")
        .plus({ days: Math.floor(minuteOfDay / MINUTES_PER_DAY) });

      return fromLuxon(
        day.set({
          hour: Math.floor(minute / MINUTES_PER_HOUR),
          minute: minute % MINUTES_PER_HOUR,
        }),
      );
    },
    plus: (config: PlusConfig) => fromLuxon(instance.plus(config)),
    minus: (config: PlusConfig) => fromLuxon(instance.minus(config)),
    difference: (other, unit, diffOpts?: DifferenceOptions) => {
      const roundingMethod = ROUNDING_METHODS[diffOpts?.rounding ?? "truncate"];

      switch (unit) {
        case "hours": {
          const value = instance.diff(toLuxonDate(other), "hours").get("hours");
          return roundingMethod(value);
        }
        case "days": {
          const value = instance.diff(toLuxonDate(other), "days").get("days");
          return roundingMethod(value);
        }
        case "years": {
          const value = instance.diff(toLuxonDate(other), "years").get("years");
          return roundingMethod(value);
        }
        case "minutes": {
          const value = instance
            .diff(toLuxonDate(other), "minutes")
            .get("minutes");
          return roundingMethod(value);
        }
        case "months": {
          const value = instance
            .diff(toLuxonDate(other), "months")
            .get("months");
          return roundingMethod(value);
        }
        case "weeks": {
          const value = instance.diff(toLuxonDate(other), "weeks").get("weeks");
          return roundingMethod(value);
        }
        case "calendarDays": {
          const value = instance
            .startOf("day")
            .diff(toLuxonDate(other, options).startOf("day"), "days")
            .get("days");
          /**
           * Rounding method is knowingly ignored here as calendar days are
           * always related to the day in the calendar. No time is involved.
           **/
          return Math.round(value);
        }
      }
    },
    toJSDate: () => instance.toJSDate(),
    toISOString: () => instance.toISO() as string,
    startOf: (unit: TemporalUnit) => fromLuxon(instance.startOf(unit)),
    endOf: (unit: TemporalUnit) => fromLuxon(instance.endOf(unit)),
    isSame: (other, unit) =>
      instance.hasSame(toLuxonDate(other, options), unit),
  };
}

function toLuxonDate(date: DateLikeInput, options: DateTimeOptions = {}) {
  let luxonDateTime: LuxonDateTime = LuxonDateTime.invalid("Not initialized");

  if (date instanceof LuxonDateTime) {
    luxonDateTime = date;
  } else if (date instanceof Date) {
    luxonDateTime = LuxonDateTime.fromJSDate(date, { zone: options.timeZone });
  } else if (isString(date)) {
    luxonDateTime = LuxonDateTime.fromISO(date, { zone: options.timeZone });
  } else {
    luxonDateTime = date.toLuxonDateTime();
  }

  return luxonDateTime.setLocale(options.locale ?? DEFAULT_LOCALE);
}
